import Phaser from "phaser";
import {
  CARDS,
  CHAPTER_ONE,
  FREE_BATTLE_SUPPLY,
  OPENING_IMPRESSIONS,
  RULES,
  SERVANTS,
  STORY_CHAPTERS,
  STAGES,
  getCard,
  getDevotion,
  getFreeBattleSupply,
  getReplayableCard,
  getServant,
  type Lane,
  type HourResolution,
  type OnlineCredentials,
  type OnlineRoomSnapshot,
  type PlayerIndex,
  type ResolutionEvent,
  type ScenarioDefinition,
  type ScenarioResultKey,
  type StoryEntry,
} from "../shared/index.js";
import {
  AppRouter,
  type AppRoute,
  type StoryChapterRoute,
} from "./app/AppRouter.js";
import {
  AppSettingsStore,
  type AnimationSpeed,
  type StorySpeed,
} from "./app/AppSettings.js";
import { AudioDirector, type AudioDebugState } from "./audio/AudioDirector.js";
import {
  BGM_CATALOG,
  BGM_CUES,
  SE_CATALOG,
  SE_CUES,
  type BgmCueId,
  type SeCueId,
} from "./audio/AudioCue.js";
import type { BattleUiState } from "./app/BattleSession.js";
import type { BattleSession } from "./app/BattleSession.js";
import { ChapterBattleSession } from "./app/ChapterBattleSession.js";
import { OnlineBattleSession } from "./app/OnlineBattleSession.js";
import {
  DEFAULT_SOLO_BATTLE_OPTIONS,
  SoloBattleSession,
  type SoloBattleOptions,
} from "./app/SoloBattleSession.js";
import { StoryDirector } from "./app/StoryDirector.js";
import {
  OnlineApiClient,
  clearOnlineCredentials,
  loadOnlineCredentials,
  saveOnlineCredentials,
} from "./online/OnlineApiClient.js";
import { getCardArtUrl } from "./assets/cardArt.js";
import { MENU_ART, getMenuServantArt } from "./assets/menuArt.js";
import { getStoryPortraitUrl } from "./assets/storyArt.js";
import { createGame } from "./game/createGame.js";
import { GAME_EVENTS, SCENE_KEYS } from "./game/constants.js";
import { BattleScene } from "./game/scenes/BattleScene.js";
import {
  EXTRA_OUTER_WINDOW,
  getStoryForChapter,
} from "./story/storyChapters.js";
import {
  buildStoryEpilogue,
  buildStoryPrelude,
  isStorySuccess,
} from "./story/storyFlow.js";
import { getChapterOneTutorial } from "./tutorial/chapterOneTutorial.js";
import "./styles/main.css";

const frontShell = requireElement<HTMLElement>("#front-shell");
const titleScreen = requireElement<HTMLElement>("#title-screen");
const menuScreen = requireElement<HTMLElement>("#menu-screen");
const subpageScreen = requireElement<HTMLElement>("#subpage-screen");
const battleScreen = requireElement<HTMLElement>("#battle-screen");
const subpageTitle = requireElement<HTMLElement>("#subpage-title");
const subpageEyebrow = requireElement<HTMLElement>("#subpage-eyebrow");
const subpageContent = requireElement<HTMLElement>("#subpage-content");
const subpageBack = requireElement<HTMLAnchorElement>("#subpage-back");
const subpageSettings = requireElement<HTMLAnchorElement>("#subpage-settings");
const menuStoryProgress = requireElement<HTMLElement>("#menu-story-progress");
const menuContinue = requireElement<HTMLAnchorElement>("#menu-continue");
const eventLog = requireElement<HTMLOListElement>("#event-log");
const battleGoal = requireElement<HTMLElement>("#battle-goal");
const battlePhaseLabel = requireElement<HTMLElement>("#battle-phase-label");
const firstPlayerLabel = requireElement<HTMLElement>("#first-player-label");
const handList = requireElement<HTMLElement>("#hand-list");
const handArea = requireElement<HTMLElement>(".hand-area");
const handCount = requireElement<HTMLElement>("#hand-count");
const turnLabel = requireElement<HTMLElement>("#turn-label");
const planningKicker = requireElement<HTMLElement>("#planning-kicker");
const tutorialPrompt = requireElement<HTMLElement>("#tutorial-prompt");
const battleTutorialOverlay = requireElement<HTMLElement>("#battle-tutorial-overlay");
const battleTutorialPortraitImage = requireElement<HTMLImageElement>("#battle-tutorial-portrait-image");
const battleTutorialSpeaker = requireElement<HTMLElement>("#battle-tutorial-speaker");
const battleTutorialRole = requireElement<HTMLElement>("#battle-tutorial-role");
const battleTutorialStep = requireElement<HTMLElement>("#battle-tutorial-step");
const battleTutorialText = requireElement<HTMLElement>("#battle-tutorial-text");
const battleTutorialProgress = requireElement<HTMLElement>("#battle-tutorial-progress");
const battleTutorialNext = requireElement<HTMLButtonElement>("#battle-tutorial-next");
const detailArtWrap = requireElement<HTMLElement>("#detail-art-wrap");
const detailArt = requireElement<HTMLImageElement>("#detail-art");
const detailSymbol = requireElement<HTMLElement>("#detail-symbol");
const detailTitle = requireElement<HTMLElement>("#detail-title");
const detailText = requireElement<HTMLElement>("#detail-text");
const detailValues = requireElement<HTMLElement>("#detail-values");
const cardDetailPopover = requireElement<HTMLElement>("#card-detail-popover");
const cardDetailClose = requireElement<HTMLButtonElement>("#card-detail-close");
const devotionButton = requireElement<HTMLButtonElement>("#devotion-button");
const devotionName = requireElement<HTMLElement>("#devotion-name");
const devotionPlayerTargets = requireElement<HTMLElement>("#devotion-player-targets");
const devotionPlayerButtons = Array.from(
  document.querySelectorAll<HTMLButtonElement>("[data-devotion-player]"),
);
const targetPrompt = requireElement<HTMLElement>("#target-prompt");
const focusHelp = requireElement<HTMLElement>("#focus-help");
const detailPanelHeading = requireElement<HTMLElement>("#detail-panel-heading");
const planningPanel = requireElement<HTMLElement>("#planning-panel");
const resolutionPanel = requireElement<HTMLElement>("#resolution-panel");
const resolutionPlans = requireElement<HTMLElement>("#resolution-plans");
const resolutionProgressLabel = requireElement<HTMLElement>("#resolution-progress-label");
const resolutionCurrentText = requireElement<HTMLElement>("#resolution-current-text");
const resolutionTimelineSteps = Array.from(
  document.querySelectorAll<HTMLElement>("[data-resolution-step]"),
);
const resourcePreviewLabel = requireElement<HTMLElement>("#resource-preview-label");
const resourcePreviewValue = requireElement<HTMLElement>("#resource-preview-value");
const resourcePreviewNote = requireElement<HTMLElement>("#resource-preview-note");
const submitButton = requireElement<HTMLButtonElement>("#submit-button");
const passButton = requireElement<HTMLButtonElement>("#pass-button");
const resetButton = requireElement<HTMLButtonElement>("#reset-button");
const battleSettingsButton = requireElement<HTMLButtonElement>("#battle-settings-button");
const battleSettingsDialog = requireElement<HTMLDialogElement>("#battle-settings-dialog");
const battleExitButton = requireElement<HTMLButtonElement>("#battle-exit-button");
const battleExitDialog = requireElement<HTMLDialogElement>("#battle-exit-dialog");
const battleExitTitle = requireElement<HTMLElement>("#battle-exit-title");
const battleExitCopy = requireElement<HTMLElement>("#battle-exit-copy");
const battleRulesLink = requireElement<HTMLAnchorElement>("#battle-rules-link");
const resultOverlay = requireElement<HTMLElement>("#result-overlay");
const resultTitle = requireElement<HTMLElement>("#result-title");
const resultDetail = requireElement<HTMLElement>("#result-detail");
const resultScores = requireElement<HTMLElement>("#result-scores");
const resultRestart = requireElement<HTMLButtonElement>("#result-restart");
const focusButtons = Array.from(document.querySelectorAll<HTMLButtonElement>("[data-focus]"));
const speedButtons = Array.from(document.querySelectorAll<HTMLButtonElement>("[data-speed]"));
const storyOverlay = requireElement<HTMLElement>("#story-overlay");
const storyNextButton = requireElement<HTMLButtonElement>("#story-next-button");
const storySkipButton = requireElement<HTMLButtonElement>("#story-skip-button");
const storyAutoButton = requireElement<HTMLButtonElement>("#story-auto-button");
const storyHistoryButton = requireElement<HTMLButtonElement>("#story-history-button");
const storyHistoryDialog = requireElement<HTMLDialogElement>("#story-history-dialog");
const storyChapterMark = requireElement<HTMLElement>(".story-chapter-mark strong");

const STORY_SCENARIOS: readonly ScenarioDefinition[] = Object.freeze([
  CHAPTER_ONE,
  ...STORY_CHAPTERS,
]);

const STORY_BGM_BY_SCENARIO: Readonly<Record<string, BgmCueId>> = Object.freeze({
  "chapter-01": "chapter_01",
  "chapter-02": "chapter_02",
  "chapter-03": "chapter_03",
  "chapter-04": "chapter_04",
  "chapter-05": "chapter_05",
  "chapter-06": "chapter_06",
});

const FREE_BATTLE_BGM_BY_STAGE: Readonly<Record<string, BgmCueId>> = Object.freeze({
  "looping-street": "chapter_01",
  "dragon-adventure": "chapter_02",
  fireside: "chapter_03",
  "winter-plaza": "chapter_04",
  "rain-study": "chapter_05",
  "layered-stage": "chapter_06",
});

const STORY_SE_CUE_ALIASES: Readonly<Record<string, SeCueId>> = Object.freeze({
  applause: "applause",
  water: "window_touch",
});

let latestUiState: BattleUiState | null = null;
let openingPlayed = false;
let storyResultHandled = false;
let storyResultSucceeded = false;
let storyTurnFourResonance: boolean | null = null;
let skipStoryOpeningOnReset = false;
let battleTutorialTurn = 0;
let battleTutorialIndex = 0;
let battleTutorialLines: readonly string[] = [];
let game: Phaser.Game | null = null;
let activeBattleMode: "story" | "solo" | "online" | null = null;
let activeScenario: ScenarioDefinition = CHAPTER_ONE;
let activeStory = getStoryForChapter(CHAPTER_ONE.id);
let soloResultShown = false;
let soloBattleOptions: SoloBattleOptions = { ...DEFAULT_SOLO_BATTLE_OPTIONS };
let pendingOnlineSnapshot: OnlineRoomSnapshot | null = null;
let activeOnlineSession: OnlineBattleSession | null = null;
let onlineSetupPoll: ReturnType<typeof setTimeout> | null = null;
let onlineRouteRequest = 0;
let activeResolution: HourResolution | null = null;
let activeResolutionEvent: ResolutionEvent | null = null;
let activeResolutionEventIndex = -1;
let activeResolutionEventTotal = 0;
let activeResolutionStage = 0;
let detailPreviewCardId: string | null = null;
let detailPreviewAnchor: HTMLElement | null = null;
let cardDetailDismissed = false;
const router = new AppRouter();
const onlineApi = new OnlineApiClient();
const settingsStore = new AppSettingsStore();
const audioDirector = new AudioDirector(settingsStore.value);
const storyDirector = new StoryDirector(activeStory, {
  overlay: storyOverlay,
  backdrop: requireElement<HTMLElement>("#story-backdrop"),
  portrait: requireElement<HTMLElement>("#story-portrait"),
  portraitImage: requireElement<HTMLImageElement>("#story-portrait-image"),
  portraitSymbol: requireElement<HTMLElement>("#story-portrait-symbol"),
  speaker: requireElement<HTMLElement>("#story-speaker"),
  speakerRole: requireElement<HTMLElement>("#story-speaker-role"),
  text: requireElement<HTMLElement>("#story-text"),
  progress: requireElement<HTMLElement>("#story-line-progress"),
  autoButton: storyAutoButton,
  historyDialog: storyHistoryDialog,
  historyList: requireElement<HTMLOListElement>("#story-history-list"),
  onEntry: handleStoryAudioEntry,
  onVisibilityChange: (visible) => {
    const scene = getActiveBattleScene();
    if (scene) scene.input.enabled = !visible;
  },
});
frontShell.style.setProperty("--front-table-art", "url(\"" + MENU_ART.table + "\")");
frontShell.style.setProperty("--front-theater-art", "url(\"" + MENU_ART.theater + "\")");
requireElement<HTMLImageElement>("#title-logo-art").src = MENU_ART.titleLogo;
setRandomTitleCharacter();
(window as Window & { __YOAKE_AUDIO_DEBUG__?: () => AudioDebugState }).__YOAKE_AUDIO_DEBUG__ = (
  () => audioDirector.debugState
);

settingsStore.subscribe((settings) => {
  audioDirector.updateSettings(settings);
  storyDirector.setAutoSpeed(settings.storySpeed);
  for (const button of speedButtons) {
    button.classList.toggle("selected", Number(button.dataset.speed) === settings.animationSpeed);
  }
  const scene = getActiveBattleScene();
  scene?.setAnimationSpeed(settings.animationSpeed);
});

router.start(renderRoute);

document.addEventListener("click", (event) => {
  const control = (event.target as Element).closest<HTMLElement>("a[href], button, select, input");
  if (!control || control.matches(":disabled")) return;
  if (control.matches("[data-audio-test-control], #story-next-button")) return;
  const confirm = control.matches(
    "#title-enter, .front-primary-command, .primary-command, button[type='submit']",
  );
  audioDirector.playSe(confirm ? "ui_confirm" : "ui_select");
});

handList.addEventListener("click", (event) => {
  const button = (event.target as Element).closest<HTMLButtonElement>("[data-card-key]");
  if (!button || button.disabled) return;
  detailPreviewCardId = null;
  detailPreviewAnchor = null;
  cardDetailDismissed = false;
  getActiveBattleScene()?.selectCard(button.dataset.cardKey ?? "");
});

handList.addEventListener("pointerover", (event) => {
  const button = (event.target as Element).closest<HTMLButtonElement>("[data-card-id]");
  const related = event.relatedTarget;
  if (!button || related instanceof Node && button.contains(related)) return;
  previewCardDetail(button);
});

handList.addEventListener("pointerout", (event) => {
  const button = (event.target as Element).closest<HTMLButtonElement>("[data-card-id]");
  const related = event.relatedTarget;
  if (!button || related instanceof Node && button.contains(related)) return;
  clearCardDetailPreview(button);
});

handList.addEventListener("focusin", (event) => {
  const button = (event.target as Element).closest<HTMLButtonElement>("[data-card-id]");
  if (button) previewCardDetail(button);
});

handList.addEventListener("focusout", (event) => {
  const button = (event.target as Element).closest<HTMLButtonElement>("[data-card-id]");
  if (button) clearCardDetailPreview(button);
});

handList.addEventListener("scroll", () => positionCardDetailPopover(detailPreviewAnchor), {
  passive: true,
});

for (const button of focusButtons) {
  button.addEventListener("click", () => {
    const lane = Number(button.dataset.focus);
    if (lane === 1 || lane === 2 || lane === 3) getActiveBattleScene()?.selectFocus(lane);
  });
}

devotionButton.addEventListener("click", () => {
  detailPreviewCardId = null;
  detailPreviewAnchor = devotionButton;
  cardDetailDismissed = false;
  getActiveBattleScene()?.selectDevotion();
});

for (const button of devotionPlayerButtons) {
  button.addEventListener("click", () => {
    const playerIndex = Number(button.dataset.devotionPlayer);
    if (playerIndex === 0 || playerIndex === 1) {
      getActiveBattleScene()?.selectDevotionTarget(playerIndex);
    }
  });
}

submitButton.addEventListener("click", async () => {
  await getActiveBattleScene()?.submitAction();
});

passButton.addEventListener("click", async () => {
  await getActiveBattleScene()?.submitPass();
});

battleTutorialNext.addEventListener("click", () => advanceBattleTutorial());

cardDetailClose.addEventListener("click", () => {
  cardDetailDismissed = true;
  hideCardDetailPopover();
});

battleSettingsButton.addEventListener("click", () => {
  if (!battleSettingsDialog.open) battleSettingsDialog.showModal();
});

battleExitButton.addEventListener("click", () => {
  battleSettingsDialog.close();
  if (!battleExitDialog.open) battleExitDialog.showModal();
});

battleRulesLink.addEventListener("click", () => {
  battleSettingsDialog.close();
});

for (const dialog of [battleSettingsDialog, battleExitDialog]) {
  dialog.addEventListener("click", (event) => {
    if (event.target === dialog) dialog.close();
  });
}

resetButton.addEventListener("click", async () => {
  battleExitDialog.close();
  battleSettingsDialog.close();
  resultOverlay.classList.add("hidden");
  if (activeBattleMode === "online") {
    await leaveOnlineBattle();
    router.navigate("online");
    return;
  }
  skipStoryOpeningOnReset = false;
  audioDirector.setBgm(activeBattleBgm());
  await getActiveBattleScene()?.resetChapter();
});

resultRestart.addEventListener("click", async () => {
  resultOverlay.classList.add("hidden");
  if (activeBattleMode === "story") {
    if (!storyResultSucceeded) {
      skipStoryOpeningOnReset = true;
      audioDirector.setBgm(activeBattleBgm());
      getActiveBattleScene()?.resetChapter();
      return;
    }
    const next = nextStoryScenario(activeScenario.id);
    if (next) {
      router.navigate(routeForScenario(next));
    } else {
      router.navigate("story");
    }
    return;
  }
  if (activeBattleMode === "online") {
    await leaveOnlineBattle();
    router.navigate("online");
    return;
  }
  audioDirector.setBgm(activeBattleBgm());
  await getActiveBattleScene()?.resetChapter();
});

storyNextButton.addEventListener("click", () => storyDirector.advance());
storySkipButton.addEventListener("click", () => storyDirector.skip());
storyAutoButton.addEventListener("click", () => storyDirector.toggleAuto());
storyHistoryButton.addEventListener("click", () => storyDirector.showHistory());
storyOverlay.addEventListener("click", (event) => {
  const target = event.target as Element;
  if (target.closest("button")) return;
  storyDirector.advance();
});

window.addEventListener("keydown", (event) => {
  if (!storyDirector.isPlaying || storyHistoryDialog.open) return;
  if (event.key !== "Enter" && event.key !== " ") return;
  event.preventDefault();
  storyDirector.advance();
});

for (const button of speedButtons) {
  button.addEventListener("click", () => {
    const speed = Number(button.dataset.speed || 1) as AnimationSpeed;
    settingsStore.update({ animationSpeed: speed });
  });
}

window.addEventListener("resize", () => {
  if (!cardDetailPopover.classList.contains("hidden")) {
    positionCardDetailPopover(resolveDetailAnchor());
  }
});

window.addEventListener("beforeunload", () => {
  router.stop();
  game?.destroy(true);
  audioDirector.destroy();
});

function renderRoute(route: AppRoute): void {
  stopOnlineSetupPolling();
  titleScreen.classList.add("hidden");
  menuScreen.classList.add("hidden");
  subpageScreen.classList.add("hidden");

  if (route === "story-extra-outer-window") {
    renderExtraOuterWindow();
    return;
  }

  if (route === "online-battle") {
    void enterOnlineBattleRoute();
    return;
  }

  const requestedScenario = scenarioForRoute(route);
  if (requestedScenario || route === "solo-battle") {
    audioDirector.setBgm(requestedScenario
      ? bgmForScenario(requestedScenario)
      : bgmForFreeBattleStage(soloBattleOptions.stageId));
    const requestedMode = requestedScenario ? "story" : "solo";
    const switchingStoryChapter = requestedScenario
      && requestedScenario.id !== activeScenario.id;
    if (game && (activeBattleMode !== requestedMode || switchingStoryChapter)) {
      destroyBattleGame();
    }
    if (requestedScenario) {
      activeScenario = requestedScenario;
      activeStory = getStoryForChapter(requestedScenario.id);
      storyDirector.setStory(activeStory);
      storyChapterMark.textContent = requestedScenario.numberLabel + "　" + requestedScenario.title;
    }
    frontShell.classList.add("hidden");
    battleScreen.classList.remove("hidden");
    configureBattlePresentation(requestedMode, requestedScenario ?? activeScenario);
    document.title = requestedMode === "story"
      ? activeScenario.numberLabel + " " + activeScenario.title + " | 夜明けまではよい夢を"
      : "AI自由対戦 | 夜明けまではよい夢を";
    ensureBattleGame(requestedMode, requestedScenario ?? activeScenario);
    return;
  }

  destroyBattleGame();
  audioDirector.setBgm("menu");
  frontShell.classList.remove("hidden");
  battleScreen.classList.add("hidden");
  resultOverlay.classList.add("hidden");
  storyOverlay.classList.add("hidden");

  if (route === "title") {
    titleScreen.classList.remove("hidden");
    document.title = "夜明けまではよい夢を";
    return;
  }

  if (route === "menu") {
    updateMenuProgress();
    menuScreen.classList.remove("hidden");
    document.title = "今夜の演目 | 夜明けまではよい夢を";
    return;
  }

  subpageScreen.classList.remove("hidden");
  renderSubpage(route);
}

function renderSubpage(route: AppRoute): void {
  subpageSettings.classList.toggle("hidden", route === "settings");
  subpageBack.href = route === "story" || route === "solo-setup" ? "#/solo" : "#/menu";

  if (route === "solo") {
    setSubpageHeading("SOLO MODE", "ソロモード");
    subpageContent.innerHTML = `
      <section class="mode-introduction">
        <p>一人で《窓》へ向かう夜を選びます。</p>
      </section>
      <div class="mode-options">
        <a class="mode-option available" href="#/story">
          <span class="mode-number">01</span>
          <div><strong>ストーリー</strong><p>新人しもべとして、夢の劇場の演目をたどる。</p></div>
          <i aria-hidden="true">›</i>
        </a>
        <a class="mode-option available" href="#/solo/setup">
          <span class="mode-number">02</span>
          <div><strong>AI対戦</strong><p>${availableStageNames()}から舞台を選び、しもべの手つきを試す。</p></div>
          <i aria-hidden="true">›</i>
        </a>
      </div>`;
    return;
  }

  if (route === "solo-setup") {
    setSubpageHeading("FREE MATCH", "AI対戦の準備");
    renderSoloSetupPage();
    return;
  }

  if (route === "story") {
    setSubpageHeading("STORY", "演目を選ぶ");
    const progress = readStoryProgress();
    const extraUnlocked = Boolean(progress.chapters["chapter-06"]?.completed);
    const extraEntry = extraUnlocked
      ? `<a class="chapter-entry available extra" href="#/story/extra-outer-window" data-story-extra="${EXTRA_OUTER_WINDOW.id}">
          <span class="chapter-number">EXTRA</span>
          <div class="chapter-entry-copy">
            <span class="eyebrow">AFTER CURTAIN</span>
            <strong>${escapeHtml(EXTRA_OUTER_WINDOW.title)}</strong>
            <p>${escapeHtml(EXTRA_OUTER_WINDOW.description)}</p>
          </div>
          <span class="chapter-state">鑑賞</span>
        </a>`
      : `<article class="chapter-entry unavailable extra" aria-disabled="true" data-story-extra="${EXTRA_OUTER_WINDOW.id}">
          <span class="chapter-number">EXTRA</span>
          <div class="chapter-entry-copy">
            <span class="eyebrow">AFTER CURTAIN</span>
            <strong>${escapeHtml(EXTRA_OUTER_WINDOW.title)}</strong>
            <p>第六章を上演すると解放されます。</p>
          </div>
          <span class="chapter-state">未解放</span>
        </article>`;
    subpageContent.innerHTML = `
      <section class="chapter-list" aria-label="章一覧">
        ${STORY_SCENARIOS.map((scenario) => {
          const completed = Boolean(progress.chapters[scenario.id]?.completed);
          const copy = chapterListCopy(scenario.id);
          return `<a class="chapter-entry available" href="#/story/chapter-${String(scenario.chapter).padStart(2, "0")}">
            <span class="chapter-number">${escapeHtml(scenario.numberLabel)}</span>
            <div class="chapter-entry-copy">
              <span class="eyebrow">${escapeHtml(copy.eyebrow)}</span>
              <strong>${escapeHtml(scenario.title)}</strong>
              <p>${escapeHtml(copy.description)}</p>
            </div>
            <span class="chapter-state">${completed ? "再演" : "開演"}</span>
          </a>`;
        }).join("")}
        ${extraEntry}
      </section>`;
    return;
  }

  if (route === "online") {
    setSubpageHeading("PRIVATE MATCH", "オンライン対戦");
    renderOnlineSetupPage();
    return;
  }

  if (route === "rules") {
    setSubpageHeading("RULE BOOK", "《窓》のルール");
    renderRulesPage();
    return;
  }

  if (route === "cards") {
    setSubpageHeading("CARD ARCHIVE", "カード一覧");
    renderCardsPage("all");
    return;
  }

  if (route === "servants") {
    setSubpageHeading("SERVANTS", "しもべ一覧");
    renderServantsPage();
    return;
  }

  setSubpageHeading("CONFIG", "設定");
  renderSettingsPage();
}

function renderExtraOuterWindow(): void {
  if (!readStoryProgress().chapters["chapter-06"]?.completed) {
    router.navigate("story");
    return;
  }

  destroyBattleGame();
  audioDirector.setBgm("chapter_06");
  frontShell.classList.remove("hidden");
  battleScreen.classList.add("hidden");
  resultOverlay.classList.add("hidden");
  titleScreen.classList.add("hidden");
  menuScreen.classList.add("hidden");
  subpageScreen.classList.remove("hidden");
  subpageSettings.classList.add("hidden");
  subpageBack.href = "#/story";
  storyChapterMark.textContent = EXTRA_OUTER_WINDOW.numberLabel
    + "　" + EXTRA_OUTER_WINDOW.title;
  setSubpageHeading("EXTRA", EXTRA_OUTER_WINDOW.title);
  subpageContent.innerHTML = `
    <section class="mode-introduction">
      <p>${escapeHtml(EXTRA_OUTER_WINDOW.description)}</p>
    </section>`;

  storyDirector.setStory(EXTRA_OUTER_WINDOW.story);
  storyDirector.play(EXTRA_OUTER_WINDOW.sequence, () => {
    router.navigate("story");
  });
}

function setSubpageHeading(eyebrow: string, title: string): void {
  subpageEyebrow.textContent = eyebrow;
  subpageTitle.textContent = title;
  document.title = title + " | 夜明けまではよい夢を";
}

function renderSoloSetupPage(): void {
  const stages = STAGES.filter((stage) => !stage.storyOnly);
  const servants = SERVANTS.filter((servant) => !servant.storyOnly);
  const selectedOpening = soloBattleOptions.openingObjectCardId;
  subpageContent.innerHTML = `
    <form id="solo-setup-form" class="solo-setup-form">
      <section class="solo-setup-fields">
        <label class="setup-select-field">
          <span>舞台</span>
          <select name="stageId">${stages.map((stage) => `<option value="${stage.id}"${stage.id === soloBattleOptions.stageId ? " selected" : ""}>${escapeHtml(stage.name)}</option>`).join("")}</select>
          <small>共通札6種に、その舞台だけの特殊札4種が加わります。</small>
        </label>
        <label class="setup-select-field">
          <span>あなたのしもべ</span>
          <select name="playerServantId">${servants.map((servant) => `<option value="${servant.id}"${servant.id === soloBattleOptions.playerServantId ? " selected" : ""}>${escapeHtml(servant.name)} · ${escapeHtml(servant.devotion.name)}</option>`).join("")}</select>
          <small>一夜に一度、カードの代わりに献身を使えます。</small>
        </label>
        <label class="setup-select-field">
          <span>AIのしもべ</span>
          <select name="opponentServantId">${servants.map((servant) => `<option value="${servant.id}"${servant.id === soloBattleOptions.opponentServantId ? " selected" : ""}>${escapeHtml(servant.name)} · ${escapeHtml(servant.devotion.name)}</option>`).join("")}</select>
          <small>AIも同じ演出卓と演出力を使います。</small>
        </label>
        <label class="setup-select-field">
          <span>幕開けの心象</span>
          <select name="openingObjectCardId">
            <option value="random"${selectedOpening === "random" ? " selected" : ""}>ランダム</option>
            <option value="none"${selectedOpening === null ? " selected" : ""}>なし</option>
            ${OPENING_IMPRESSIONS.map((entry) => {
              const card = getCard(entry.cardId);
              return card ? `<option value="${entry.cardId}"${selectedOpening === entry.cardId ? " selected" : ""}>${escapeHtml(card.name)} · ${escapeHtml(entry.interpretation)}</option>` : "";
            }).join("")}
          </select>
          <small>選ばれた心象は、最初からL2D2に浮かんでいます。</small>
        </label>
      </section>
      <aside class="solo-rules-preview">
        <span class="eyebrow">OPEN SUPPLY RULE</span>
        <h2>公開札の一夜</h2>
        <dl>
          <div><dt>演出力</dt><dd>初期3 · 毎時間+3 · 上限6</dd></div>
          <div><dt>基本札</dt><dd>何度でも使用可能</dd></div>
          <div><dt>特殊札</dt><dd>各しもべ一度だけ</dd></div>
          <div><dt>舞台札</dt><dd id="stage-special-card-list"></dd></div>
          <div><dt>行動</dt><dd>一時間にカード1枚か献身</dd></div>
        </dl>
        <button class="front-primary-command" type="submit">この演目を始める</button>
      </aside>
    </form>`;

  const stageSelect = requireElement<HTMLSelectElement>('select[name="stageId"]');
  const stageSpecialCardList = requireElement<HTMLElement>("#stage-special-card-list");
  const updateStageSpecialCards = (): void => {
    stageSpecialCardList.textContent = getFreeBattleSupply(stageSelect.value)
      .filter((entry) => entry.limited)
      .map((entry) => {
        const card = getCard(entry.cardId);
        return card ? "《" + card.name + "》" + entry.cost : "";
      })
      .filter(Boolean)
      .join(" · ");
  };
  stageSelect.addEventListener("change", updateStageSpecialCards);
  updateStageSpecialCards();

  requireElement<HTMLFormElement>("#solo-setup-form").addEventListener("submit", (event) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget as HTMLFormElement);
    const opening = String(form.get("openingObjectCardId") ?? "random");
    soloBattleOptions = {
      stageId: String(form.get("stageId") ?? DEFAULT_SOLO_BATTLE_OPTIONS.stageId),
      playerServantId: String(form.get("playerServantId") ?? DEFAULT_SOLO_BATTLE_OPTIONS.playerServantId),
      opponentServantId: String(form.get("opponentServantId") ?? DEFAULT_SOLO_BATTLE_OPTIONS.opponentServantId),
      openingObjectCardId: opening === "none" ? null : opening,
      seed: Date.now(),
    };
    router.navigate("solo-battle");
  });
}

function renderOnlineSetupPage(): void {
  const credentials = loadOnlineCredentials();
  if (credentials && pendingOnlineSnapshot?.status === "waiting") {
    renderOnlineWaitingPage(pendingOnlineSnapshot);
    startOnlineSetupPolling(credentials);
    return;
  }

  const stages = STAGES.filter((stage) => !stage.storyOnly);
  const servants = SERVANTS.filter((servant) => !servant.storyOnly);
  subpageContent.innerHTML = `
    <section class="online-setup online-match-setup">
      <div class="setup-copy">
        <span class="eyebrow">PASSWORD ROOM</span>
        <h2>合言葉で《窓》をつなぐ</h2>
        <p>同じ合言葉を入力した二人が、自由対戦と同じ演出卓で競います。希望する舞台が異なるときは、二つの候補から一つが選ばれます。</p>
        <dl class="online-rule-notes">
          <div><dt>計画</dt><dd>両者が提出するまで非公開</dd></div>
          <div><dt>再接続</dt><dd>同じタブの再読み込みに対応</dd></div>
          <div><dt>退室</dt><dd>残った一人は次の参加者を待機</dd></div>
        </dl>
      </div>
      <form id="online-setup-form" class="setup-form online-setup-form">
        <label><span>表示名</span><input name="name" type="text" maxlength="20" required placeholder="しもべの名前"></label>
        <label><span>合言葉</span><input name="phrase" type="text" maxlength="32" required autocomplete="off" placeholder="二人だけの合言葉"></label>
        <label><span>しもべ</span><select name="servantId">${servants.map((servant) => `<option value="${servant.id}">${escapeHtml(servant.name)} · ${escapeHtml(servant.devotion.name)}</option>`).join("")}</select></label>
        <label><span>希望する舞台</span><select name="stageId">${stages.map((stage) => `<option value="${stage.id}">${escapeHtml(stage.name)} · ${escapeHtml(stage.tagline)}</option>`).join("")}</select></label>
        <button type="submit" class="front-primary-command">部屋へ入る</button>
        <small id="online-form-status" class="form-status">同じ合言葉の相手を一人だけ招待できます。</small>
        ${credentials ? `<button id="online-resume" type="button" class="front-secondary-command">前の部屋へ戻る</button>
          <button id="online-forget" type="button" class="quiet-command">前の接続情報を消す</button>` : ""}
      </form>
    </section>`;

  requireElement<HTMLFormElement>("#online-setup-form").addEventListener("submit", async (event) => {
    event.preventDefault();
    const formElement = event.currentTarget as HTMLFormElement;
    const button = formElement.querySelector<HTMLButtonElement>('button[type="submit"]');
    const status = requireElement<HTMLElement>("#online-form-status");
    const form = new FormData(formElement);
    if (button) button.disabled = true;
    status.textContent = "部屋を探しています…";
    try {
      if (credentials) {
        try { await onlineApi.leave(credentials); } catch { /* The old room may already be gone. */ }
      }
      clearOnlineCredentials();
      const joined = await onlineApi.join({
        name: String(form.get("name") ?? ""),
        phrase: String(form.get("phrase") ?? ""),
        servantId: String(form.get("servantId") ?? "hourglass"),
        stageId: String(form.get("stageId") ?? "looping-street"),
      });
      saveOnlineCredentials(joined.credentials);
      pendingOnlineSnapshot = joined.room;
      if (joined.room.status === "playing" || joined.room.status === "finished") {
        router.navigate("online-battle");
      } else {
        renderOnlineWaitingPage(joined.room);
        startOnlineSetupPolling(joined.credentials);
      }
    } catch (error) {
      status.textContent = error instanceof Error ? error.message : "部屋へ接続できませんでした。";
      if (button) button.disabled = false;
    }
  });

  subpageContent.querySelector<HTMLButtonElement>("#online-resume")?.addEventListener("click", () => {
    if (!credentials) return;
    void resumeOnlineRoom(credentials);
  });
  subpageContent.querySelector<HTMLButtonElement>("#online-forget")?.addEventListener("click", async () => {
    if (credentials) {
      try { await onlineApi.leave(credentials); } catch { /* Expired rooms need no cleanup. */ }
    }
    clearOnlineCredentials();
    pendingOnlineSnapshot = null;
    renderOnlineSetupPage();
  });
}

function renderOnlineWaitingPage(snapshot: OnlineRoomSnapshot): void {
  const viewer = snapshot.viewerIndex;
  const own = snapshot.players[viewer];
  const other = snapshot.players[viewer === 0 ? 1 : 0];
  subpageContent.innerHTML = `
    <section class="online-waiting" aria-live="polite">
      <span class="online-waiting-symbol" aria-hidden="true">窓</span>
      <span class="eyebrow">WAITING FOR ANOTHER SERVANT</span>
      <h2>合言葉の向こう側を待っています</h2>
      <p>${other ? escapeHtml(other.name) + "と演目を準備しています。" : "同じ合言葉を、もう一人のプレイヤーに伝えてください。"}</p>
      <dl>
        <div><dt>あなた</dt><dd>${escapeHtml(own?.name ?? "待機中")}</dd></div>
        <div><dt>しもべ</dt><dd>${escapeHtml(getServant(own?.servantId)?.name ?? "—")}</dd></div>
        <div><dt>希望舞台</dt><dd>${escapeHtml(STAGES.find((stage) => stage.id === own?.preferredStageId)?.name ?? "—")}</dd></div>
      </dl>
      <button id="online-wait-cancel" type="button" class="front-secondary-command">部屋を出る</button>
    </section>`;
  subpageContent.querySelector<HTMLButtonElement>("#online-wait-cancel")?.addEventListener("click", async () => {
    await leaveOnlineBattle();
    renderOnlineSetupPage();
  });
}

async function resumeOnlineRoom(credentials: OnlineCredentials): Promise<void> {
  try {
    const snapshot = await onlineApi.getState(credentials);
    pendingOnlineSnapshot = snapshot;
    if (snapshot.status === "playing" || snapshot.status === "finished") {
      router.navigate("online-battle");
    } else {
      renderOnlineWaitingPage(snapshot);
      startOnlineSetupPolling(credentials);
    }
  } catch (error) {
    clearOnlineCredentials();
    pendingOnlineSnapshot = null;
    renderOnlineSetupPage();
    const status = subpageContent.querySelector<HTMLElement>("#online-form-status");
    if (status) status.textContent = error instanceof Error ? error.message : "前の部屋は終了しています。";
  }
}

function startOnlineSetupPolling(credentials: OnlineCredentials): void {
  stopOnlineSetupPolling();
  const poll = async (): Promise<void> => {
    try {
      const snapshot = await onlineApi.getState(credentials);
      pendingOnlineSnapshot = snapshot;
      if (snapshot.status === "playing" || snapshot.status === "finished") {
        router.navigate("online-battle");
        return;
      }
      onlineSetupPoll = setTimeout(() => void poll(), 900);
    } catch (error) {
      clearOnlineCredentials();
      pendingOnlineSnapshot = null;
      renderOnlineSetupPage();
      const status = subpageContent.querySelector<HTMLElement>("#online-form-status");
      if (status) status.textContent = error instanceof Error ? error.message : "部屋との接続が切れました。";
    }
  };
  onlineSetupPoll = setTimeout(() => void poll(), 500);
}

function stopOnlineSetupPolling(): void {
  if (onlineSetupPoll) clearTimeout(onlineSetupPoll);
  onlineSetupPoll = null;
}

function renderRulesPage(): void {
  subpageContent.innerHTML = `
    <div class="rules-layout">
      <nav class="rules-summary" aria-label="勝敗の基準">
        <div><span>悪夢</span><strong>0以下</strong><small>その場で敗北</small></div>
        <div class="ideal"><span>理想</span><strong>15</strong><small>もっともよい朝</small></div>
        <div><span>寝坊</span><strong>16以上</strong><small>理想から遠いほど不利</small></div>
      </nav>
      <section class="rule-section"><span>01</span><div><h2>一夜は6時間</h2><p>二人は各時間にカード1枚、献身、または何もしないことを選びます。選択は伏せられ、二人が決めた後に公開されます。</p></div></section>
      <section class="rule-section"><span>02</span><div><h2>注目するレーン</h2><p>注目は一時間に隣のレーンまで移せます。注目したレーンの夢だけが、そのご主人の幸福度へ届きます。二人が同じレーンを見れば共鳴します。</p></div></section>
      <section class="rule-section"><span>03</span><div><h2>夢の深度</h2><p>夢オブジェクトは深度3から現れ、時間の終わりに全て同時に浅くなります。深度1から進んだ夢は《窓》を去ります。</p></div></section>
      <section class="rule-section"><span>04</span><div><h2>合体と操作</h2><p>自由対戦で同じ場所へ重なった夢は、その瞬間に表示されている幸福度影響をすべて足し、固定値の《混ざった夢》になります。元の能力と状態は失われ、三つ以上の夢も一度に合体します。</p></div></section>
      <section class="rule-section"><span>05</span><div><h2>夜明け</h2><p>途中で幸福度が0以下になると悪夢です。6時間を終えたら、15に近いご主人ほどよい朝を迎えます。ただし16以上は寝坊となります。</p></div></section>
      <section class="rule-section"><span>06</span><div><h2>公開札の演出卓</h2><p>試合では手札を使わず、公開された札から一時間に1枚を選びます。演出力は3から始まり、毎時間3回復して最大6。共通札6種は何度でも、舞台ごとの特殊札4種は各しもべ一度だけ使えます。</p></div></section>
      <section class="rule-section"><span>07</span><div><h2>しもべの献身</h2><p>献身は各しもべが一夜に一度、カードの代わりに使える固有能力です。対象は夢、ご主人、直前に使ったカードなど能力ごとに異なります。詳しい効果は「しもべ一覧」で確認できます。</p></div></section>
    </div>`;
}

function renderCardsPage(filter: "all" | "object" | "spell"): void {
  const cards = CARDS.filter((card) => filter === "all" || card.kind === filter);
  subpageContent.innerHTML = `
    <div class="reference-toolbar" role="group" aria-label="カード種別">
      <button type="button" data-card-filter="all" class="${filter === "all" ? "selected" : ""}">すべて ${CARDS.length}</button>
      <button type="button" data-card-filter="object" class="${filter === "object" ? "selected" : ""}">夢オブジェクト</button>
      <button type="button" data-card-filter="spell" class="${filter === "spell" ? "selected" : ""}">操作</button>
    </div>
    <div class="card-reference-grid">
      ${cards.map((card) => {
        const art = getCardArtUrl(card.id);
        const supply = FREE_BATTLE_SUPPLY.find((entry) => entry.cardId === card.id);
        const values = card.kind === "object"
          ? `<span>D3 ${signed(card.values[3])}</span><span>D2 ${signed(card.values[2])}</span><span>D1 ${signed(card.values[1])}</span>`
          : `<span>操作カード</span>`;
        return `<article class="card-reference-item" data-card-id="${escapeHtml(card.id)}" data-kind="${card.kind}">
          <div class="card-reference-art ${art ? "has-image" : ""}">${art
            ? `<img src="${escapeHtml(art)}" alt="">`
            : `<span aria-hidden="true">${card.kind === "object" ? "夢" : "術"}</span>`}</div>
          <div><span class="eyebrow">${card.kind === "object" ? "DREAM OBJECT" : "MANIPULATION"}</span><h2>${escapeHtml(card.name)}</h2><p>${escapeHtml(card.text)}</p><div class="card-reference-values">${supply ? `<span>自由対戦 ${supply.cost}</span><span>${supply.limited ? "特殊札" : "基本札"}</span>` : ""}${values}</div></div>
        </article>`;
      }).join("")}
    </div>`;

  for (const button of subpageContent.querySelectorAll<HTMLButtonElement>("[data-card-filter]")) {
    button.addEventListener("click", () => {
      const next = button.dataset.cardFilter;
      if (next === "all" || next === "object" || next === "spell") renderCardsPage(next);
    });
  }
}

function renderServantsPage(): void {
  subpageContent.innerHTML = `
    <div class="servant-reference-list">
      ${SERVANTS.filter((servant) => !servant.storyOnly).map((servant) => {
        const art = getMenuServantArt(servant.id);
        return `<article class="servant-reference-item">
          <div class="servant-reference-art">${art ? `<img src="${escapeHtml(art)}" alt="">` : ""}</div>
          <div class="servant-reference-copy">
            <span class="eyebrow">${servant.storyOnly ? "STORY SERVANT" : "PLAYABLE SERVANT"}</span>
            <h2>${escapeHtml(servant.name)}</h2>
            <p class="servant-title">${escapeHtml(servant.title)}</p>
            <dl><dt>献身《${escapeHtml(servant.devotion.name)}》</dt><dd>${escapeHtml(servant.devotion.text)}</dd></dl>
          </div>
        </article>`;
      }).join("")}
    </div>`;
}

function renderSettingsPage(): void {
  const settings = settingsStore.value;
  subpageContent.innerHTML = `
    <form class="settings-form">
      <section class="settings-section">
        <header><span class="eyebrow">AUDIO</span><h2>音量</h2></header>
        ${volumeControl("masterVolume", "全体", settings.masterVolume)}
        ${volumeControl("bgmVolume", "BGM", settings.bgmVolume)}
        ${volumeControl("seVolume", "効果音", settings.seVolume)}
      </section>
      <section class="settings-section audio-test-section">
        <header class="audio-test-heading">
          <div><span class="eyebrow">SOUND TEST</span><h2>音を試聴する</h2></div>
          <button id="audio-test-stop" class="audio-stop-button" type="button" data-audio-test-control title="BGMを停止" aria-label="BGMを停止">■</button>
        </header>
        <p class="audio-test-intro">音源ファイルがない項目は仮音源で鳴ります。追加した素材は、画面を再読み込みするとここから確認できます。</p>
        <output id="audio-test-status" class="audio-test-status">試聴する音を選んでください。</output>
        ${renderBgmSoundTest()}
        ${renderSeSoundTest()}
      </section>
      <section class="settings-section audio-credits-section">
        <header><span class="eyebrow">AUDIO CREDITS</span><h2>音源クレジット</h2></header>
        <ul class="audio-credit-list">
          <li><span>タイトル・エンドロール</span><strong>「月の降る街」 / KK</strong><a href="https://dova-s.jp/bgm/detail/15196" target="_blank" rel="noreferrer">DOVA-SYNDROME</a></li>
          <li><span>各章BGM</span><strong>PeriTune</strong><a href="https://peritune.com/" target="_blank" rel="noreferrer">PeriTune オリジナル音楽</a></li>
          <li><span>効果音</span><strong>効果音ラボ</strong><a href="https://soundeffect-lab.info/" target="_blank" rel="noreferrer">配布サイト</a></li>
        </ul>
      </section>
      <section class="settings-section">
        <header><span class="eyebrow">PLAYBACK</span><h2>再生</h2></header>
        <div class="setting-row"><div><strong>試合演出</strong><small>カード処理と盤面移動</small></div><div class="settings-segmented" data-setting-group="animationSpeed">
          ${settingButton("0.5", "ゆっくり", settings.animationSpeed === 0.5)}
          ${settingButton("1", "標準", settings.animationSpeed === 1)}
          ${settingButton("2", "速い", settings.animationSpeed === 2)}
        </div></div>
        <div class="setting-row"><div><strong>会話の自動送り</strong><small>自動モードの待ち時間</small></div><div class="settings-segmented" data-setting-group="storySpeed">
          ${settingButton("0.8", "ゆっくり", settings.storySpeed === 0.8)}
          ${settingButton("1", "標準", settings.storySpeed === 1)}
          ${settingButton("1.35", "速い", settings.storySpeed === 1.35)}
        </div></div>
      </section>
      <div class="settings-footer">
        <span>設定はこのブラウザに保存されます。</span>
        <button id="settings-reset" class="quiet-command" type="button">初期設定へ戻す</button>
      </div>
    </form>`;

  for (const input of subpageContent.querySelectorAll<HTMLInputElement>("[data-volume-setting]")) {
    input.addEventListener("input", () => {
      const key = input.dataset.volumeSetting as "masterVolume" | "bgmVolume" | "seVolume";
      const output = input.closest(".volume-row")?.querySelector<HTMLOutputElement>("output");
      if (output) output.value = input.value + "%";
      settingsStore.update({ [key]: Number(input.value) });
    });
  }
  for (const group of subpageContent.querySelectorAll<HTMLElement>("[data-setting-group]")) {
    group.addEventListener("click", (event) => {
      const button = (event.target as Element).closest<HTMLButtonElement>("[data-setting-value]");
      if (!button) return;
      const value = Number(button.dataset.settingValue);
      if (group.dataset.settingGroup === "animationSpeed") {
        settingsStore.update({ animationSpeed: value as AnimationSpeed });
      } else {
        settingsStore.update({ storySpeed: value as StorySpeed });
      }
      for (const candidate of group.querySelectorAll("button")) {
        candidate.classList.toggle("selected", candidate === button);
      }
    });
  }
  const audioTestStatus = requireElement<HTMLOutputElement>("#audio-test-status");
  for (const button of subpageContent.querySelectorAll<HTMLButtonElement>("[data-bgm-test]")) {
    button.addEventListener("click", () => {
      const cue = button.dataset.bgmTest as BgmCueId;
      audioDirector.setBgm(cue);
      markPlayingAudioTest(button);
      const entry = BGM_CATALOG.find((candidate) => candidate.id === cue);
      audioTestStatus.value = "BGM試聴中: " + (entry?.label ?? cue)
        + (BGM_CUES[cue].provided ? "（追加素材）" : "（仮音源）");
    });
  }
  for (const button of subpageContent.querySelectorAll<HTMLButtonElement>("[data-se-test]")) {
    button.addEventListener("click", () => {
      const cue = button.dataset.seTest as SeCueId;
      audioDirector.playSe(cue);
      const entry = SE_CATALOG.find((candidate) => candidate.id === cue);
      audioTestStatus.value = "効果音を再生: " + (entry?.label ?? cue)
        + (SE_CUES[cue].provided ? "（追加素材）" : "（仮音源）");
    });
  }
  requireElement<HTMLButtonElement>("#audio-test-stop").addEventListener("click", () => {
    audioDirector.setBgm(null);
    markPlayingAudioTest(null);
    audioTestStatus.value = "BGMを停止しました。";
  });
  requireElement<HTMLButtonElement>("#settings-reset").addEventListener("click", () => {
    settingsStore.reset();
    renderSettingsPage();
  });
}

function volumeControl(key: "masterVolume" | "bgmVolume" | "seVolume", label: string, value: number): string {
  return `<label class="volume-row"><span>${label}</span><input type="range" min="0" max="100" step="1" value="${value}" data-volume-setting="${key}"><output>${value}%</output></label>`;
}

function settingButton(value: string, label: string, selected: boolean): string {
  return `<button type="button" data-setting-value="${value}" class="${selected ? "selected" : ""}">${label}</button>`;
}

function renderBgmSoundTest(): string {
  const categories = [...new Set(BGM_CATALOG.map((entry) => entry.category))];
  return `<div class="audio-test-library"><h3>BGM</h3>${categories.map((category) => `
    <section class="audio-test-group">
      <h4>${escapeHtml(category)}</h4>
      <div class="audio-test-list">${BGM_CATALOG.filter((entry) => entry.category === category).map((entry) => audioTestRow(
        "bgm",
        entry.id,
        entry.label,
        entry.usage,
        entry.suggestedLength,
        BGM_CUES[entry.id].provided,
      )).join("")}</div>
    </section>`).join("")}</div>`;
}

function renderSeSoundTest(): string {
  const categories = [...new Set(SE_CATALOG.map((entry) => entry.category))];
  return `<div class="audio-test-library"><h3>効果音</h3>${categories.map((category) => `
    <section class="audio-test-group">
      <h4>${escapeHtml(category)}</h4>
      <div class="audio-test-list">${SE_CATALOG.filter((entry) => entry.category === category).map((entry) => audioTestRow(
        "se",
        entry.id,
        entry.label,
        entry.usage,
        entry.suggestedLength,
        SE_CUES[entry.id].provided,
      )).join("")}</div>
    </section>`).join("")}</div>`;
}

function audioTestRow(
  kind: "bgm" | "se",
  id: string,
  label: string,
  usage: string,
  suggestedLength: string,
  provided: boolean,
): string {
  const dataAttribute = kind === "bgm" ? "data-bgm-test" : "data-se-test";
  return `<article class="audio-test-row">
    <button class="audio-preview-button" type="button" ${dataAttribute}="${escapeHtml(id)}" data-audio-test-control title="${escapeHtml(label)}を再生" aria-label="${escapeHtml(label)}を再生">▶</button>
    <div class="audio-test-copy">
      <strong>${escapeHtml(label)}</strong>
      <small>${escapeHtml(usage)}</small>
      <code>${kind}/${escapeHtml(id)}.*</code>
    </div>
    <div class="audio-test-meta">
      <span class="audio-source-state ${provided ? "provided" : "placeholder"}">${provided ? "素材あり" : "仮音源"}</span>
      <small>${escapeHtml(suggestedLength)}</small>
    </div>
  </article>`;
}

function markPlayingAudioTest(active: HTMLButtonElement | null): void {
  for (const button of subpageContent.querySelectorAll<HTMLButtonElement>("[data-bgm-test]")) {
    button.classList.toggle("playing", button === active);
    button.textContent = button === active ? "♪" : "▶";
  }
}

function availableStageNames(): string {
  return STAGES.filter((stage) => !stage.storyOnly).map((stage) => `《${stage.shortName}》`).join("、");
}

function setRandomTitleCharacter(): void {
  const candidates = SERVANTS
    .filter((servant) => !servant.storyOnly)
    .map((servant) => ({ name: servant.name, art: getMenuServantArt(servant.id) }))
    .filter((candidate) => Boolean(candidate.art));
  const selected = candidates[Math.floor(Math.random() * candidates.length)]!;
  requireElement<HTMLImageElement>("#title-character-art").src = selected.art;
  const label = document.querySelector<HTMLElement>(".title-character-card figcaption strong");
  if (label) label.textContent = selected.name;
}

function updateMenuProgress(): void {
  const progress = readStoryProgress();
  const completedCount = STORY_SCENARIOS.filter(
    (scenario) => progress.chapters[scenario.id]?.completed,
  ).length;
  const next = STORY_SCENARIOS.find(
    (scenario) => !progress.chapters[scenario.id]?.completed,
  );
  menuStoryProgress.textContent = completedCount + " / " + STORY_SCENARIOS.length + " 上演済み";
  if (next) {
    menuContinue.href = "#/story/chapter-" + String(next.chapter).padStart(2, "0");
    menuContinue.textContent = next.numberLabel + "を始める";
    requireElement<HTMLElement>("#menu-notice-title").textContent = next.numberLabel + "「" + next.title + "」";
    return;
  }
  menuContinue.href = "#/story";
  menuContinue.textContent = "演目を選ぶ";
  requireElement<HTMLElement>("#menu-notice-title").textContent = "全六章 上演済み";
}

interface ChapterProgressEntry {
  completed: boolean;
  result?: string | null;
  playerHappiness?: number;
  opponentHappiness?: number;
  completedAt?: string;
}

interface StoryProgressData {
  chapters: Record<string, ChapterProgressEntry>;
}

function readStoryProgress(): StoryProgressData {
  try {
    const data = JSON.parse(window.localStorage.getItem("yoake-phaser-story-v1") ?? "null") as {
      chapters?: Record<string, ChapterProgressEntry>;
      chapter01?: ChapterProgressEntry;
    } | null;
    if (data?.chapters) return { chapters: { ...data.chapters } };
    if (data?.chapter01) {
      return { chapters: { "chapter-01": { ...data.chapter01 } } };
    }
  } catch {
    // A fresh progress record is used when storage is unavailable or malformed.
  }
  return { chapters: {} };
}

function configureBattlePresentation(
  mode: "story" | "solo" | "online",
  scenario: ScenarioDefinition,
): void {
  battleScreen.dataset.mode = mode;
  const onlineState = mode === "online" ? pendingOnlineSnapshot?.state ?? null : null;
  const onlineViewer = pendingOnlineSnapshot?.viewerIndex ?? 0;
  const onlineOpponent: PlayerIndex = onlineViewer === 0 ? 1 : 0;
  const stageId = mode === "online" ? onlineState?.stageId : soloBattleOptions.stageId;
  const stage = mode !== "story" ? STAGES.find((item) => item.id === stageId) : null;
  const playerServant = mode === "solo" ? getServant(soloBattleOptions.playerServantId) : null;
  const opponentServant = mode === "solo" ? getServant(soloBattleOptions.opponentServantId) : null;
  const onlinePlayerServant = onlineState ? getServant(onlineState.players[onlineViewer].servantId) : null;
  const onlineOpponentServant = onlineState ? getServant(onlineState.players[onlineOpponent].servantId) : null;
  const storyOpponentServant = mode === "story" ? getServant(scenario.battle.servantIds[1]) : null;
  requireElement<HTMLElement>("#battle-kicker").textContent = mode === "story"
    ? "夜明けまではよい夢を"
    : mode === "online" ? "PRIVATE ONLINE MATCH" : "AI FREE MATCH";
  requireElement<HTMLElement>("#battle-title").textContent = mode === "story"
    ? scenario.numberLabel + "　" + scenario.title
    : mode === "online" ? "合言葉対戦" : "自由対戦";
  requireElement<HTMLElement>("#battle-performance-title").textContent = mode === "story"
    ? scenario.performanceTitle
    : "公開札の演目《" + (stage?.name ?? "静かな《窓》") + "》";
  requireElement<HTMLElement>("#battle-board-title").textContent = mode === "story"
    ? scenario.performanceTitle
    : stage?.name ?? "共有された《窓》";
  battleGoal.textContent = mode === "story"
    ? scenario.goal
    : "目標　夜明けに幸福度を15へ近づけ、0以下と16以上を避ける";
  requireElement<HTMLElement>("#guest-role-0").textContent = mode === "story"
    ? "新人のご主人"
    : mode === "online" ? "あなた · " + (onlineState?.players[onlineViewer].name ?? "しもべ") : "あなたのしもべ";
  requireElement<HTMLElement>("#guest-name-0").textContent = mode === "story"
    ? scenario.battle.playerNames[0]
    : mode === "online" ? onlinePlayerServant?.name ?? "あなた" : playerServant?.name ?? "あなた";
  requireElement<HTMLElement>("#guest-role-1").textContent = mode === "story"
    ? (storyOpponentServant?.name ?? "先輩") + "のご主人"
    : mode === "online" ? "相手 · " + (onlineState?.players[onlineOpponent].name ?? "しもべ") : "AIのしもべ";
  requireElement<HTMLElement>("#guest-name-1").textContent = mode === "story"
    ? scenario.battle.playerNames[1]
    : mode === "online" ? onlineOpponentServant?.name ?? "相手" : opponentServant?.name ?? "AI";
  resultRestart.textContent = mode === "story"
    ? nextStoryScenario(scenario.id) ? "次の章へ" : "演目一覧へ"
    : mode === "online" ? "部屋を出る" : "もう一度対戦する";
  const exitLabel = mode === "story"
    ? "演目を中断"
    : mode === "online" ? "部屋を出る" : "対戦を終了";
  battleExitButton.textContent = exitLabel;
  resetButton.textContent = exitLabel;
  battleExitTitle.textContent = mode === "story"
    ? "演目を中断しますか？"
    : mode === "online" ? "部屋を出ますか？" : "対戦を終了しますか？";
  battleExitCopy.textContent = mode === "online"
    ? "相手は部屋に残り、別のしもべを待つことができます。"
    : mode === "story"
      ? "この章の試合は最初からになります。"
      : "現在の試合内容は失われます。";
  passButton.textContent = mode === "story" ? "何も置かない" : "演出力を残す";
  submitButton.textContent = "この計画を決定";
}

function ensureBattleGame(mode: "story" | "solo" | "online", scenario: ScenarioDefinition): void {
  if (game) return;
  activeBattleMode = mode;
  openingPlayed = false;
  storyResultHandled = false;
  storyResultSucceeded = false;
  storyTurnFourResonance = null;
  skipStoryOpeningOnReset = false;
  soloResultShown = false;
  latestUiState = null;
  resetResolutionPresentation();
  storyDirector.reset();
  resetBattleTutorial();
  detailPreviewCardId = null;
  detailPreviewAnchor = null;
  cardDetailDismissed = false;
  hideCardDetailPopover();
  if (battleSettingsDialog.open) battleSettingsDialog.close();
  if (battleExitDialog.open) battleExitDialog.close();
  resultOverlay.classList.add("hidden");
  eventLog.innerHTML = "<li>《窓》は静かに開いています。</li>";
  let session: BattleSession;
  if (mode === "story") {
    session = new ChapterBattleSession(scenario);
  } else if (mode === "online") {
    const credentials = loadOnlineCredentials();
    if (!credentials || !pendingOnlineSnapshot?.state) return;
    activeOnlineSession = new OnlineBattleSession(pendingOnlineSnapshot, credentials, onlineApi);
    session = activeOnlineSession;
  } else {
    session = new SoloBattleSession(soloBattleOptions);
  }
  const createdGame = createGame("phaser-game", session);
  game = createdGame;
  bindGameEvents(createdGame);
}

function bindGameEvents(currentGame: Phaser.Game): void {
  currentGame.events.on(GAME_EVENTS.battleReady, () => {
    resetButton.disabled = false;
    getBattleScene(currentGame).setAnimationSpeed(settingsStore.value.animationSpeed);
  });
  currentGame.events.on(GAME_EVENTS.uiState, (uiState: BattleUiState) => {
    latestUiState = uiState;
    renderUi(uiState);
    if (uiState.mode === "story") {
      handleStoryProgress(uiState);
    } else if (uiState.phase === "result" && !soloResultShown) {
      soloResultShown = true;
      showSoloResult(uiState);
    }
    syncBattleTutorial(uiState);
  });
  currentGame.events.on(GAME_EVENTS.resolutionStart, (resolution: HourResolution) => {
    activeResolution = resolution;
    activeResolutionEvent = null;
    activeResolutionEventIndex = -1;
    activeResolutionEventTotal = resolution.events.length;
    activeResolutionStage = 0;
    renderResolutionPanel();
  });
  currentGame.events.on(
    GAME_EVENTS.resolutionEvent,
    (payload: { event: ResolutionEvent; index: number; total: number }) => {
      activeResolutionEvent = payload.event;
      activeResolutionEventIndex = payload.index;
      activeResolutionEventTotal = payload.total;
      activeResolutionStage = Math.max(
        activeResolutionStage,
        resolutionStageForEvent(payload.event, activeResolution),
      );
      renderResolutionPanel();
    },
  );
  currentGame.events.on(GAME_EVENTS.resolutionEnd, () => {
    resetResolutionPresentation();
  });
  currentGame.events.on(
    GAME_EVENTS.happiness,
    (payload: { playerIndex: 0 | 1; value: number }) => {
      updateHappiness(payload.playerIndex, payload.value);
    },
  );
  currentGame.events.on(
    GAME_EVENTS.happinessDelta,
    (payload: { playerIndex: 0 | 1; changes: number[]; speed: number }) => {
      showHappinessChanges(payload.playerIndex, payload.changes, payload.speed);
    },
  );
  currentGame.events.on(GAME_EVENTS.status, (message: string) => {
    const item = document.createElement("li");
    item.textContent = message;
    eventLog.prepend(item);
    while (eventLog.children.length > 12) eventLog.lastElementChild?.remove();
  });
  currentGame.events.on(GAME_EVENTS.logReset, () => {
    eventLog.replaceChildren();
    openingPlayed = skipStoryOpeningOnReset;
    storyResultHandled = false;
    storyResultSucceeded = false;
    storyTurnFourResonance = null;
    skipStoryOpeningOnReset = false;
    soloResultShown = false;
    resetResolutionPresentation();
    storyDirector.reset();
    resetBattleTutorial();
    resultOverlay.classList.add("hidden");
  });
  currentGame.events.on(GAME_EVENTS.audioCue, (cue: SeCueId) => {
    audioDirector.playSe(cue);
  });
}

function destroyBattleGame(): void {
  const onlineSession = activeOnlineSession;
  activeOnlineSession = null;
  if (onlineSession) {
    clearOnlineCredentials();
    pendingOnlineSnapshot = null;
    void onlineSession.leave().catch(() => undefined);
  }
  if (!game) return;
  storyDirector.reset();
  resetBattleTutorial();
  detailPreviewCardId = null;
  detailPreviewAnchor = null;
  cardDetailDismissed = false;
  hideCardDetailPopover();
  if (battleSettingsDialog.open) battleSettingsDialog.close();
  if (battleExitDialog.open) battleExitDialog.close();
  game.destroy(true);
  game = null;
  activeBattleMode = null;
  latestUiState = null;
  resetResolutionPresentation();
  delete battleScreen.dataset.phase;
  requireElement<HTMLElement>("#phaser-game").replaceChildren();
}

async function enterOnlineBattleRoute(): Promise<void> {
  const requestId = ++onlineRouteRequest;
  const credentials = loadOnlineCredentials();
  if (!credentials) {
    router.navigate("online");
    return;
  }

  try {
    if (!pendingOnlineSnapshot?.state) {
      frontShell.classList.remove("hidden");
      battleScreen.classList.add("hidden");
      subpageScreen.classList.remove("hidden");
      setSubpageHeading("PRIVATE MATCH", "部屋へ戻っています");
      subpageContent.innerHTML = `<section class="online-waiting" aria-live="polite">
        <span class="online-waiting-symbol" aria-hidden="true">窓</span>
        <h2>《窓》を開き直しています</h2><p>接続情報を確かめています…</p>
      </section>`;
    }
    const snapshot = pendingOnlineSnapshot?.roomId === credentials.roomId
      ? pendingOnlineSnapshot
      : await onlineApi.getState(credentials);
    if (requestId !== onlineRouteRequest) return;
    pendingOnlineSnapshot = snapshot;
    if (!snapshot.state || snapshot.status === "waiting") {
      router.navigate("online");
      return;
    }
    if (game && activeBattleMode !== "online") destroyBattleGame();
    audioDirector.setBgm(bgmForFreeBattleStage(snapshot.state.stageId));
    frontShell.classList.add("hidden");
    battleScreen.classList.remove("hidden");
    configureBattlePresentation("online", activeScenario);
    document.title = "合言葉対戦 | 夜明けまではよい夢を";
    ensureBattleGame("online", activeScenario);
  } catch {
    clearOnlineCredentials();
    pendingOnlineSnapshot = null;
    router.navigate("online");
  }
}

async function leaveOnlineBattle(): Promise<void> {
  stopOnlineSetupPolling();
  const session = activeOnlineSession;
  activeOnlineSession = null;
  const credentials = session?.credentials ?? loadOnlineCredentials();
  if (session) {
    try { await session.leave(); } catch { /* The room may already have expired. */ }
  } else if (credentials) {
    try { await onlineApi.leave(credentials); } catch { /* The room may already have expired. */ }
  }
  clearOnlineCredentials();
  pendingOnlineSnapshot = null;
  if (game) {
    game.destroy(true);
    game = null;
    activeBattleMode = null;
    latestUiState = null;
    resetResolutionPresentation();
    requireElement<HTMLElement>("#phaser-game").replaceChildren();
  }
}

function renderUi(ui: BattleUiState): void {
  const state = ui.state;
  const ownIndex = ui.viewerIndex;
  const opponentIndex: PlayerIndex = ownIndex === 0 ? 1 : 0;
  const player = state.players[ownIndex];
  const opponent = state.players[opponentIndex];
  const isPlanning = ui.phase === "planning";
  const isResolving = ui.phase === "resolving";
  const isWaiting = ui.phase === "waiting";
  if (ui.mode === "online") {
    const stage = STAGES.find((item) => item.id === state.stageId);
    requireElement<HTMLElement>("#guest-role-0").textContent = "あなた · " + player.name;
    requireElement<HTMLElement>("#guest-name-0").textContent = getServant(player.servantId)?.name ?? "あなた";
    requireElement<HTMLElement>("#guest-role-1").textContent = "相手 · " + opponent.name;
    requireElement<HTMLElement>("#guest-name-1").textContent = getServant(opponent.servantId)?.name ?? "相手";
    requireElement<HTMLElement>("#battle-performance-title").textContent = "公開札の演目《" + (stage?.name ?? "共有された窓") + "》";
    requireElement<HTMLElement>("#battle-board-title").textContent = stage?.name ?? "共有された《窓》";
    audioDirector.setBgm(bgmForFreeBattleStage(state.stageId));
  }
  battleScreen.dataset.phase = ui.phase;
  turnLabel.textContent = "第" + state.turn + "時間 / " + RULES.maxTurns;
  battlePhaseLabel.textContent = isResolving
    ? "解決中"
    : ui.phase === "result" ? "夜明け" : isWaiting ? "相手待ち" : "計画中";
  firstPlayerLabel.textContent = "先手 " + (state.firstPlayer === ownIndex
    ? "あなた"
    : ui.mode === "solo" ? "AI" : opponent.name);
  planningKicker.textContent = ui.phase === "resolving"
    ? "夢を処理しています"
    : ui.phase === "result"
      ? "夜明け"
      : isWaiting ? "計画を伏せて待っています" : "第" + state.turn + "時間の手つき";
  tutorialPrompt.textContent = ui.tutorialPrompt;
  targetPrompt.textContent = ui.error || ui.targetPrompt;
  targetPrompt.classList.toggle("error", Boolean(ui.error));
  planningPanel.classList.toggle("hidden", isResolving || ui.phase === "result");
  resolutionPanel.classList.toggle("hidden", !isResolving);
  handArea.classList.toggle("inactive", !isPlanning);
  detailPanelHeading.textContent = isResolving
    ? "今時間の解決"
    : ui.phase === "result" ? "夜明け" : isWaiting ? "相手の計画を待っています" : "今時間の手つき";

  updateHappiness(0, player.happiness);
  updateHappiness(1, opponent.happiness);
  renderGuestStatus(0, ownIndex, ui);
  renderGuestStatus(1, opponentIndex, ui);

  renderHand(ui);
  renderFocus(ui);
  renderResourcePreview(ui);
  syncCardDetailPopover(ui);
  if (isResolving) renderResolutionPanel();

  const servant = getServant(player.servantId);
  const selectedDevotion = ui.draft.devotionId ? servant?.devotion : null;
  devotionName.textContent = servant?.devotion.name ?? "献身";
  devotionButton.disabled = !ui.devotionAvailable;
  devotionButton.classList.toggle("selected", Boolean(ui.draft.devotionId));
  devotionButton.classList.toggle("spent", player.devotionUsed);
  const selectsPlayer = selectedDevotion?.targetType === "player";
  devotionPlayerTargets.classList.toggle("hidden", !selectsPlayer);
  devotionPlayerButtons.forEach((button, displayIndex) => {
    const playerIndex = displayIndex === 0 ? ownIndex : opponentIndex;
    button.dataset.devotionPlayer = String(playerIndex);
    const target = state.players[playerIndex];
    button.textContent = displayIndex === 0
      ? "自分側（" + (target?.name ?? "ご主人") + "）"
      : "相手側（" + (target?.name ?? "ご主人") + "）";
    button.classList.toggle("selected", ui.draft.target.playerIndex === playerIndex);
    button.disabled = !selectsPlayer || ui.phase !== "planning"
      || target?.happiness === RULES.idealHappiness;
  });
  submitButton.disabled = !ui.canSubmit;
  passButton.disabled = !ui.canPass;
  resetButton.disabled = ui.phase === "resolving";
  battleExitButton.disabled = ui.phase === "resolving";

}

function renderGuestStatus(
  displayIndex: 0 | 1,
  playerIndex: PlayerIndex,
  ui: BattleUiState,
): void {
  const player = ui.state.players[playerIndex];
  const energy = ui.energy?.[playerIndex];
  const energyLabel = requireElement<HTMLElement>("#player-energy-" + displayIndex);
  energyLabel.textContent = energy === undefined
    ? String(player.hand.length)
    : energy + "/" + ui.maxEnergy;

  const conditions: string[] = [];
  if (player.focusLockedUntilTurn >= ui.state.turn) conditions.push("注目固定");
  if (ui.mode === "online") {
    if (!ui.connected[playerIndex]) conditions.push("退室");
    else if (ui.submitted[playerIndex]) conditions.push("提出済み");
    else conditions.push(displayIndex === 0 ? "行動選択中" : "計画中");
  } else if (ui.phase === "resolving") {
    conditions.push("夢を解決中");
  } else if (ui.phase === "result") {
    conditions.push("夜明け");
  } else {
    conditions.push(displayIndex === 0 ? "行動選択中" : ui.mode === "solo" ? "AI思考中" : "計画中");
  }
  requireElement<HTMLElement>("#player-meta-" + displayIndex).textContent = conditions.join("・");
}

function handleStoryProgress(ui: BattleUiState): void {
  if (storyDirector.isPlaying || ui.phase === "resolving") return;
  const story = activeStory;

  if (!openingPlayed) {
    openingPlayed = true;
    audioDirector.playSe("curtain_open");
    storyDirector.play(buildStoryPrelude(story), () => {
      if (latestUiState) syncBattleTutorial(latestUiState);
    });
    return;
  }

  const resolution = ui.state.lastResolution;
  const resolvedTurn = resolution?.initialFrame.turn ?? 0;
  if (resolution && resolvedTurn === 4) {
    storyTurnFourResonance = resolution.resonance;
  }

  if (ui.phase !== "result" || storyResultHandled) return;
  storyResultHandled = true;
  const resultKey = ui.resultKey ?? "low";
  storyResultSucceeded = isStorySuccess(resultKey);
  if (!storyResultSucceeded) {
    showResult(ui, false);
    return;
  }

  storyDirector.play(
    buildStoryEpilogue(story, resultKey, storyTurnFourResonance),
    () => showResult(ui, true),
  );
}

function syncBattleTutorial(ui: BattleUiState): void {
  const shouldOpen = activeBattleMode === "story"
    && activeScenario.id === "chapter-01"
    && ui.phase === "planning"
    && !ui.state.result
    && !storyDirector.isPlaying;
  if (!shouldOpen) {
    if (ui.phase !== "planning" || activeScenario.id !== "chapter-01") {
      closeBattleTutorial();
    }
    return;
  }
  if (battleTutorialTurn === ui.state.turn) return;

  const sequence = getChapterOneTutorial(
    ui.state.turn,
    ui.state.players[0].happiness,
  );
  if (!sequence) return;
  battleTutorialTurn = ui.state.turn;
  battleTutorialIndex = 0;
  battleTutorialLines = sequence.lines;
  battleTutorialSpeaker.textContent = sequence.speaker;
  battleTutorialRole.textContent = sequence.role;
  battleTutorialStep.textContent = "手ほどき " + ui.state.turn + " / " + RULES.maxTurns;
  battleTutorialPortraitImage.src = getStoryPortraitUrl(sequence.portraitId);
  battleTutorialPortraitImage.alt = "";
  renderBattleTutorialLine();
  battleTutorialOverlay.classList.remove("hidden");
  battleTutorialNext.focus();
}

function advanceBattleTutorial(): void {
  if (battleTutorialOverlay.classList.contains("hidden")) return;
  if (battleTutorialIndex + 1 < battleTutorialLines.length) {
    battleTutorialIndex += 1;
    renderBattleTutorialLine();
    return;
  }
  closeBattleTutorial();
  handList.querySelector<HTMLButtonElement>("button:not(:disabled)")?.focus();
}

function renderBattleTutorialLine(): void {
  battleTutorialText.textContent = battleTutorialLines[battleTutorialIndex] ?? "";
  battleTutorialProgress.textContent = String(battleTutorialIndex + 1)
    + " / " + String(battleTutorialLines.length);
}

function closeBattleTutorial(): void {
  battleTutorialOverlay.classList.add("hidden");
}

function resetBattleTutorial(): void {
  closeBattleTutorial();
  battleTutorialTurn = 0;
  battleTutorialIndex = 0;
  battleTutorialLines = [];
  battleTutorialPortraitImage.removeAttribute("src");
}

function renderHand(ui: BattleUiState): void {
  if (ui.cardSource === "supply") {
    renderSupply(ui);
    return;
  }
  handList.classList.remove("supply-mode");
  const player = ui.state.players[ui.viewerIndex];
  const allowed = new Set(ui.allowedCardInstanceIds);
  requireElement<HTMLElement>("#card-source-label").textContent = "手札";
  handCount.textContent = player.hand.length + "枚";
  handList.innerHTML = player.hand.map((instance) => {
    const card = getCard(instance.cardId);
    if (!card) return "";
    const image = getCardArtUrl(card.id);
    const selected = ui.draft.cardInstanceId === instance.instanceId;
    const disabled = ui.phase !== "planning" || !allowed.has(instance.instanceId);
    const valueLabel = card.kind === "object"
      ? "D3 " + signed(card.values[3]) + " / D2 " + signed(card.values[2]) + " / D1 " + signed(card.values[1])
      : "操作";
    return "<button class=\"hand-card" + (selected ? " selected" : "") + "\""
      + " type=\"button\" data-card-key=\"" + escapeHtml(instance.instanceId) + "\""
      + " data-card-instance=\"" + escapeHtml(instance.instanceId) + "\""
      + " data-card-id=\"" + escapeHtml(card.id) + "\""
      + (disabled ? " disabled" : "") + ">"
      + (image
        ? "<img src=\"" + escapeHtml(image) + "\" alt=\"\">"
        : "<span class=\"hand-card-fallback\" aria-hidden=\"true\">夢</span>")
      + "<strong>" + escapeHtml(card.name) + "</strong>"
      + "<small>" + escapeHtml(valueLabel) + "</small>"
      + "</button>";
  }).join("");
}

function renderSupply(ui: BattleUiState): void {
  handList.classList.add("supply-mode");
  requireElement<HTMLElement>("#card-source-label").textContent = "演出卓";
  handCount.textContent = "演出力 " + (ui.energy?.[ui.viewerIndex] ?? 0) + "/" + ui.maxEnergy;
  handList.innerHTML = ui.supplyCards.map((option) => {
    const card = getCard(option.cardId);
    if (!card) return "";
    const image = getCardArtUrl(card.id);
    const selected = ui.draft.supplyCardId === card.id;
    const seals = option.limited
      ? "自" + (option.available ? "○" : "済") + "・相" + (option.opponentAvailable ? "○" : "済")
      : "基本札 ∞";
    return "<button class=\"hand-card supply-card" + (selected ? " selected" : "") + "\""
      + " type=\"button\" data-card-key=\"" + escapeHtml(card.id) + "\""
      + " data-card-id=\"" + escapeHtml(card.id) + "\""
      + " title=\"" + escapeHtml(card.name) + "\""
      + (option.available ? "" : " disabled") + ">"
      + "<span class=\"supply-cost\" title=\"演出力\">" + option.cost + "</span>"
      + (image
        ? "<img src=\"" + escapeHtml(image) + "\" alt=\"\">"
        : "<span class=\"hand-card-fallback\" aria-hidden=\"true\">" + (card.kind === "object" ? "夢" : "術") + "</span>")
      + "<strong>" + escapeHtml(card.name) + "</strong>"
      + "<small>" + escapeHtml(seals) + "</small>"
      + "</button>";
  }).join("");
}

function renderFocus(ui: BattleUiState): void {
  for (const button of focusButtons) {
    const lane = Number(button.dataset.focus) as Lane;
    button.classList.toggle("selected", ui.draft.focusLane === lane);
    button.disabled = ui.phase !== "planning" || !ui.legalFocusLanes.includes(lane);
  }
  const legal = ui.legalFocusLanes.map((lane) => "L" + lane).join("・");
  const player = ui.state.players[ui.viewerIndex];
  focusHelp.textContent = player.focusLockedUntilTurn >= ui.state.turn
    ? "《満たす一杯》により、この時間の注目はL" + player.focusLane + "に固定されています。"
    : "現在の位置から選べるレーン: " + legal;
}

function renderDetail(ui: BattleUiState, previewCardId: string | null = null): void {
  const player = ui.state.players[ui.viewerIndex];
  const instance = player.hand.find(
    (item) => item.instanceId === ui.draft.cardInstanceId,
  );
  const card = previewCardId
    ? getCard(previewCardId)
    : ui.draft.supplyCardId
      ? getCard(ui.draft.supplyCardId)
      : instance ? getCard(instance.cardId) : null;
  const servant = getServant(player.servantId);
  const devotion = previewCardId ? null : ui.draft.devotionId ? servant?.devotion : null;

  if (card) {
    const image = getCardArtUrl(card.id);
    setDetailArt(image, card.kind === "object" ? "夢" : "術");
    detailTitle.textContent = card.name;
    detailText.textContent = card.text;
    const supplyOption = ui.supplyCards.find((entry) => entry.cardId === card.id);
    const availabilityMarkup = supplyOption
      ? "<span class=\"availability-mark " + (supplyOption.limited ? "limited" : "basic") + "\""
        + " title=\"" + (supplyOption.limited ? "特殊札：一度だけ使用できます" : "基本札：何度でも使用できます") + "\""
        + " aria-label=\"" + (supplyOption.limited ? "特殊札、一度だけ使用可能" : "基本札、何度でも使用可能") + "\">"
        + "<strong aria-hidden=\"true\">" + (supplyOption.limited ? "!" : "∞") + "</strong>"
        + "<small>" + (supplyOption.limited ? "特殊" : "基本") + "</small></span>"
      : "";
    const costMarkup = supplyOption
      ? "<span class=\"cost-mark\" aria-label=\"コスト " + supplyOption.cost + "\">"
        + "<small>コスト</small><strong>" + supplyOption.cost + "</strong></span>"
      : "";
    const cardKindLabel = card.kind === "object" ? "夢オブジェクト" : "操作カード";
    const kindMarkup = "<span class=\"kind-mark " + card.kind + "\""
      + " title=\"" + cardKindLabel + "\" aria-label=\"種類：" + cardKindLabel + "\">"
      + "<small>種類</small><strong aria-hidden=\"true\">"
      + (card.kind === "object" ? "夢" : "術")
      + "</strong></span>";
    const depthMarkup = card.kind === "object"
      ? "<div class=\"depth-impact\">"
        + "<div class=\"depth-impact-heading\"><span>深度別幸福値</span><small>深い → 浅い</small></div>"
        + "<div class=\"depth-impact-grid\">"
        + renderDepthImpact(3, card.values[3])
        + renderDepthImpact(2, card.values[2])
        + renderDepthImpact(1, card.values[1])
        + "</div></div>"
      : "";
    detailValues.innerHTML = "<div class=\"detail-meta-row\">"
      + availabilityMarkup + costMarkup + kindMarkup + "</div>" + depthMarkup;
    return;
  }
  if (devotion) {
    const replayCard = devotion.targetType === "replay"
      ? getReplayableCard(ui.state, ui.viewerIndex)
      : null;
    setDetailArt(replayCard ? getCardArtUrl(replayCard.id) : "", "献");
    detailTitle.textContent = "献身《" + devotion.name + "》";
    detailText.textContent = replayCard
      ? devotion.text + " 今回の再演: 《" + replayCard.name + "》"
      : devotion.text;
    detailValues.innerHTML = "<div class=\"detail-meta-row\">"
      + "<span class=\"availability-mark devotion\" title=\"献身：一夜に一度\">"
      + "<strong aria-hidden=\"true\">1</strong><small>一夜</small></span>"
      + "<span class=\"kind-mark devotion\" title=\"献身\" aria-label=\"種類：献身\">"
      + "<small>種類</small><strong aria-hidden=\"true\">献</strong></span></div>"
      + "<div class=\"detail-note-strip\">カードの代わりに使用"
      + (replayCard ? " · 再演《" + escapeHtml(replayCard.name) + "》" : "") + "</div>";
    return;
  }
  setDetailArt("", "窓");
  detailTitle.textContent = "静かな《窓》";
  detailText.textContent = "注目したレーンにある夢だけが、そのご主人の幸福度へ届きます。";
  detailValues.innerHTML = "<div class=\"detail-rule-grid\">"
    + "<span><small>悪夢</small><strong>0以下</strong></span>"
    + "<span class=\"ideal\"><small>理想</small><strong>15</strong></span>"
    + "<span class=\"oversleep\"><small>寝坊</small><strong>16以上</strong></span></div>";
}

function previewCardDetail(button: HTMLButtonElement): void {
  const cardId = button.dataset.cardId;
  if (!cardId || !latestUiState) return;
  detailPreviewCardId = cardId;
  detailPreviewAnchor = button;
  cardDetailDismissed = false;
  renderDetail(latestUiState, cardId);
  showCardDetailPopover(button, false);
}

function clearCardDetailPreview(button: HTMLButtonElement): void {
  if (detailPreviewAnchor !== button) return;
  detailPreviewCardId = null;
  detailPreviewAnchor = null;
  if (latestUiState) syncCardDetailPopover(latestUiState);
}

function syncCardDetailPopover(ui: BattleUiState): void {
  if (ui.phase === "resolving" || ui.phase === "result") {
    hideCardDetailPopover();
    return;
  }
  if (detailPreviewCardId) {
    renderDetail(ui, detailPreviewCardId);
    showCardDetailPopover(detailPreviewAnchor, false);
    return;
  }

  const hasSelectedCard = Boolean(ui.draft.cardInstanceId || ui.draft.supplyCardId);
  const hasSelectedDevotion = Boolean(ui.draft.devotionId);
  if ((!hasSelectedCard && !hasSelectedDevotion) || cardDetailDismissed) {
    hideCardDetailPopover();
    return;
  }

  renderDetail(ui);
  const anchor = hasSelectedDevotion
    ? devotionButton
    : handList.querySelector<HTMLElement>(".hand-card.selected");
  detailPreviewAnchor = anchor;
  showCardDetailPopover(anchor, true);
}

function showCardDetailPopover(anchor: HTMLElement | null, pinned: boolean): void {
  cardDetailPopover.classList.remove("hidden");
  cardDetailPopover.classList.toggle("pinned", pinned);
  positionCardDetailPopover(anchor);
}

function hideCardDetailPopover(): void {
  cardDetailPopover.classList.add("hidden");
  cardDetailPopover.classList.remove("pinned");
}

function resolveDetailAnchor(): HTMLElement | null {
  if (detailPreviewCardId) {
    return handList.querySelector<HTMLElement>(
      "[data-card-id=\"" + CSS.escape(detailPreviewCardId) + "\"]",
    );
  }
  if (latestUiState?.draft.devotionId) return devotionButton;
  return handList.querySelector<HTMLElement>(".hand-card.selected");
}

function positionCardDetailPopover(anchor: HTMLElement | null): void {
  if (cardDetailPopover.classList.contains("hidden")) return;
  window.requestAnimationFrame(() => {
    if (cardDetailPopover.classList.contains("hidden")) return;
    const margin = 10;
    const popover = cardDetailPopover.getBoundingClientRect();
    const target = anchor?.isConnected ? anchor.getBoundingClientRect() : null;
    const boardPanel = document.querySelector<HTMLElement>(".battle-panel")?.getBoundingClientRect();
    const minimumLeft = Math.max(margin, (boardPanel?.left ?? margin) + 8);
    const maximumLeft = Math.min(
      window.innerWidth - popover.width - margin,
      (boardPanel?.right ?? window.innerWidth) - popover.width - 8,
    );
    let left = target
      ? target.left + target.width / 2 - popover.width / 2
      : window.innerWidth / 2 - popover.width / 2;
    left = Math.max(minimumLeft, Math.min(Math.max(minimumLeft, maximumLeft), left));

    let top = target ? target.top - popover.height - 12 : margin;
    if (top < margin && target) top = target.bottom + 12;
    top = Math.max(margin, Math.min(window.innerHeight - popover.height - margin, top));
    cardDetailPopover.style.left = Math.round(left) + "px";
    cardDetailPopover.style.top = Math.round(top) + "px";
  });
}

function renderDepthImpact(depth: 1 | 2 | 3, value: number): string {
  const tone = value > 0 ? "positive" : value < 0 ? "negative" : "neutral";
  return "<span class=\"depth-impact-cell " + tone + "\">"
    + "<small>深度 " + depth + "</small><strong>" + signed(value) + "</strong></span>";
}

function renderResourcePreview(ui: BattleUiState): void {
  const player = ui.state.players[ui.viewerIndex];
  const instance = player.hand.find((item) => item.instanceId === ui.draft.cardInstanceId);
  const card = ui.draft.supplyCardId
    ? getCard(ui.draft.supplyCardId)
    : instance ? getCard(instance.cardId) : null;
  const actionSelected = Boolean(card || ui.draft.devotionId);

  if (ui.energy) {
    const current = ui.energy[ui.viewerIndex];
    const cost = card
      ? ui.supplyCards.find((entry) => entry.cardId === card.id)?.cost ?? 0
      : 0;
    resourcePreviewLabel.textContent = "現在の演出力";
    resourcePreviewValue.textContent = actionSelected ? current + " → " + (current - cost) : String(current);
    resourcePreviewNote.textContent = "次の時間 +" + RULES.openSupplyRecovery + " / 最大 " + ui.maxEnergy;
    return;
  }

  const after = card ? Math.max(0, player.hand.length - 1) : player.hand.length;
  resourcePreviewLabel.textContent = "手札";
  resourcePreviewValue.textContent = card ? player.hand.length + " → " + after + "枚" : player.hand.length + "枚";
  resourcePreviewNote.textContent = "山札 " + player.deck.length + " / 捨て札 " + player.discard.length;
}

function renderResolutionPanel(): void {
  const resolution = activeResolution;
  if (!resolution) {
    resolutionPlans.replaceChildren();
    resolutionProgressLabel.textContent = "処理を開始します";
    resolutionCurrentText.textContent = "二人の計画を公開します。";
    updateResolutionTimeline(0, false);
    return;
  }

  const viewerIndex = latestUiState?.viewerIndex ?? 0;
  resolutionPlans.innerHTML = resolution.order.map((playerIndex, orderIndex) => {
    const plan = resolution.plans[playerIndex];
    const actionName = resolutionActionName(resolution, playerIndex);
    const actor = playerIndex === viewerIndex
      ? "あなた"
      : activeBattleMode === "solo" ? "AI" : resolution.initialFrame.players[playerIndex].name;
    const target = resolution.targetLabels[playerIndex]
      ? " · " + resolution.targetLabels[playerIndex]
      : "";
    return "<article class=\"resolution-plan " + (playerIndex === viewerIndex ? "player" : "opponent") + "\">"
      + "<span>" + (orderIndex === 0 ? "先手" : "後手") + "　" + escapeHtml(actor) + "</span>"
      + "<strong>" + escapeHtml(actionName) + "</strong>"
      + "<small>注目 L" + plan.focusLane + escapeHtml(target) + "</small>"
      + "</article>";
  }).join("");

  updateResolutionTimeline(activeResolutionStage, activeResolutionEventIndex >= 0);
  resolutionProgressLabel.textContent = activeResolutionEventIndex >= 0
    ? "処理 " + (activeResolutionEventIndex + 1) + " / " + activeResolutionEventTotal
    : "計画を公開しています";
  resolutionCurrentText.textContent = activeResolutionEvent?.text ?? "二人の計画を公開します。";
  planningKicker.textContent = "解決 " + (activeResolutionStage + 1) + " / 5";
  tutorialPrompt.textContent = activeResolutionEvent?.text ?? "二人の計画を公開しています。";
}

function updateResolutionTimeline(activeStep: number, hasStarted: boolean): void {
  for (const item of resolutionTimelineSteps) {
    const step = Number(item.dataset.resolutionStep);
    item.classList.toggle("current", step === activeStep);
    item.classList.toggle("complete", hasStarted && step < activeStep);
  }
}

function resolutionActionName(resolution: HourResolution, playerIndex: 0 | 1): string {
  const card = getCard(resolution.cardIds[playerIndex]);
  if (card) return card.name;
  const devotion = getDevotion(resolution.devotionIds[playerIndex]);
  if (devotion) return "献身《" + devotion.name + "》";
  return activeBattleMode === "solo" ? "演出力を残す" : "何も置かない";
}

function resolutionStageForEvent(
  event: ResolutionEvent,
  resolution: HourResolution | null,
): number {
  if (event.type === "reveal" || event.type === "resonance") return 0;
  if (event.type === "happiness" || event.type === "comfort") return 3;
  if (event.type === "hold" || event.type === "drift" || event.type === "result") return 4;
  if (!resolution || event.playerIndex === undefined) return activeResolutionStage;
  if (event.playerIndex === resolution.order[0]) return 1;
  if (event.playerIndex === resolution.order[1]) return 2;
  return activeResolutionStage;
}

function resetResolutionPresentation(): void {
  activeResolution = null;
  activeResolutionEvent = null;
  activeResolutionEventIndex = -1;
  activeResolutionEventTotal = 0;
  activeResolutionStage = 0;
  renderResolutionPanel();
}

function setDetailArt(url: string, symbol: string): void {
  detailArtWrap.classList.toggle("has-image", Boolean(url));
  detailArt.src = url;
  detailArt.alt = "";
  detailSymbol.textContent = symbol;
}

function showResult(ui: BattleUiState, succeeded: boolean): void {
  const state = ui.state;
  const resultKey = ui.resultKey ?? "low";
  resultTitle.textContent = activeScenario.numberLabel + "「" + activeScenario.title + "」"
    + (succeeded ? "成功" : "未完");
  resultDetail.textContent = succeeded
    ? chapterResultMessage(activeScenario, resultKey)
    : chapterResultMessage(activeScenario, resultKey)
      + " 主人公の幸福度を13〜15に整えると、物語の後編へ進めます。";
  resultScores.innerHTML = state.players.map((player, index) => {
    const servant = getServant(player.servantId);
    const servantName = activeBattleMode === "story" && index === 0
      ? "新人"
      : servant?.name ?? "しもべ";
    return "<article><span>" + escapeHtml(servantName) + " · " + escapeHtml(player.name) + "</span>"
      + "<strong>" + player.happiness + "</strong>"
      + "<small>理想との差 " + Math.abs(player.happiness - RULES.idealHappiness) + "</small></article>";
  }).join("");
  if (succeeded) {
    writeChapterProgress(activeScenario, ui);
    resultRestart.textContent = nextStoryScenario(activeScenario.id) ? "次の章へ" : "演目一覧へ";
    if (activeBattleMode !== "story") audioDirector.setBgm("result");
    audioDirector.playSe("result_ideal");
  } else {
    resultRestart.textContent = "もう一度演じる";
    audioDirector.setBgm("nightmare");
    audioDirector.playSe(resultKey === "oversleep" ? "result_oversleep" : "result_nightmare");
  }
  resultOverlay.classList.remove("hidden");
}

function showSoloResult(ui: BattleUiState): void {
  const state = ui.state;
  const result = state.result;
  const ownIndex = ui.viewerIndex;
  const opponentIndex: PlayerIndex = ownIndex === 0 ? 1 : 0;
  const ownWinner = ownIndex === 0 ? "p1" : "p2";
  const opponentWinner = ownIndex === 0 ? "p2" : "p1";
  resultTitle.textContent = result?.winner === ownWinner
    ? "あなたのご主人が、よりよい朝へ"
    : result?.winner === opponentWinner
      ? (ui.mode === "online" ? "相手" : "AI") + "のご主人が、よりよい朝へ"
      : "二人は同じ朝へたどり着いた";
  resultDetail.textContent = result
    ? result.title + "。" + result.detail
    : "一夜の演目が終わりました。";
  resultScores.innerHTML = ([ownIndex, opponentIndex] as const).map((index, displayIndex) => {
    const player = state.players[index];
    const servant = getServant(player.servantId);
    const label = displayIndex === 0 ? "あなた" : ui.mode === "online" ? "相手" : "AI";
    return "<article><span>" + label + " · " + escapeHtml(servant?.name ?? player.name) + "</span>"
      + "<strong>" + player.happiness + "</strong>"
      + "<small>理想との差 " + Math.abs(player.happiness - RULES.idealHappiness) + "</small></article>";
  }).join("");
  audioDirector.setBgm("result");
  audioDirector.playSe("time_chime");
  resultOverlay.classList.remove("hidden");
}

function writeChapterProgress(scenario: ScenarioDefinition, ui: BattleUiState): void {
  try {
    const progress = readStoryProgress();
    progress.chapters[scenario.id] = {
        completed: true,
        result: ui.resultKey,
        playerHappiness: ui.state.players[0].happiness,
        opponentHappiness: ui.state.players[1].happiness,
        completedAt: new Date().toISOString(),
    };
    window.localStorage.setItem("yoake-phaser-story-v1", JSON.stringify(progress));
  } catch {
    // The current play session remains available when browser storage is disabled.
  }
}

function scenarioForRoute(route: AppRoute): ScenarioDefinition | null {
  if (!route.startsWith("story-chapter-")) return null;
  const chapter = Number(route.slice("story-chapter-".length));
  return STORY_SCENARIOS.find((scenario) => scenario.chapter === chapter) ?? null;
}

function routeForScenario(scenario: ScenarioDefinition): StoryChapterRoute {
  return ("story-chapter-" + String(scenario.chapter).padStart(2, "0")) as StoryChapterRoute;
}

function nextStoryScenario(chapterId: string): ScenarioDefinition | null {
  const index = STORY_SCENARIOS.findIndex((scenario) => scenario.id === chapterId);
  return index >= 0 ? STORY_SCENARIOS[index + 1] ?? null : STORY_SCENARIOS[0] ?? null;
}

function bgmForScenario(scenario: ScenarioDefinition): BgmCueId {
  return STORY_BGM_BY_SCENARIO[scenario.id] ?? "battle";
}

function bgmForFreeBattleStage(stageId: string | undefined): BgmCueId {
  return FREE_BATTLE_BGM_BY_STAGE[stageId ?? ""] ?? "battle";
}

function activeBattleBgm(): BgmCueId {
  return activeBattleMode === "story"
    ? bgmForScenario(activeScenario)
    : bgmForFreeBattleStage(activeBattleMode === "online"
      ? pendingOnlineSnapshot?.state?.stageId
      : soloBattleOptions.stageId);
}

function handleStoryAudioEntry(entry: StoryEntry): void {
  if (entry.cue?.startsWith("bgm:")) {
    const cue = entry.cue.slice(4) as BgmCueId;
    if (cue in BGM_CUES) audioDirector.setBgm(cue);
    audioDirector.playSe("scene_transition");
    return;
  }

  const explicitCue = entry.cue ? STORY_SE_CUE_ALIASES[entry.cue] : undefined;
  audioDirector.playSe(explicitCue ?? (entry.kind === "title"
    ? "scene_transition"
    : "dialogue_advance"));
}

function chapterListCopy(chapterId: string): { eyebrow: string; description: string } {
  const copies: Readonly<Record<string, { eyebrow: string; description: string }>> = {
    "chapter-01": {
      eyebrow: "帰らずの散歩道",
      description: "男と老犬が共有する、どこまでも続く散歩の夢。",
    },
    "chapter-02": {
      eyebrow: "竜の待つ山",
      description: "少年と少女が、傷ついたドラゴンの待つ山頂を目指す。",
    },
    "chapter-03": {
      eyebrow: "鈴の鳴らないサンタ",
      description: "正解を持たない贈り手と、彼を待つ子供の聖夜。",
    },
    "chapter-04": {
      eyebrow: "鳴りやまないアンコール",
      description: "冬のピアノに閉じ込められた、演奏者の記憶を読み替える。",
    },
    "chapter-05": {
      eyebrow: "積み重なった部屋",
      description: "同じ場所を掃除し続けるロボットの時間を見届ける。",
    },
    "chapter-06": {
      eyebrow: "再演",
      description: "六つの演目を携え、止まっていた次の一音を待つ。",
    },
  };
  return copies[chapterId] ?? { eyebrow: "DREAM THEATER", description: "今宵の演目。" };
}

function chapterResultMessage(
  scenario: ScenarioDefinition,
  resultKey: ScenarioResultKey,
): string {
  const tone = {
    ideal: "二人の幸福度は、夜明けにちょうどよく着地しました。",
    oversleep: "夢は甘く満ちすぎましたが、演目は最後の場面へたどり着きました。",
    low: "余韻は少し冷たいまま、それでも演目は朝へ何かを残しました。",
    nightmare: "夜は先に砕けました。それでも劇場には、読み直せる筋書きが残っています。",
  }[resultKey];
  return scenario.goal + "。" + tone;
}

function updateHappiness(playerIndex: 0 | 1, value: number): void {
  requireElement<HTMLElement>("#happiness-value-" + playerIndex).textContent = String(value);
  const readout = requireElement<HTMLElement>("#happiness-readout-" + playerIndex);
  const state = requireElement<HTMLElement>("#happiness-state-" + playerIndex);
  const tone = value <= RULES.nightmareLine
    ? "nightmare"
    : value === RULES.idealHappiness
      ? "ideal"
      : value >= RULES.oversleepLine ? "oversleep" : "seeking";
  readout.classList.remove("nightmare", "ideal", "oversleep", "seeking");
  readout.classList.add(tone);
  state.textContent = tone === "nightmare"
    ? "悪夢"
    : tone === "ideal"
      ? "理想"
      : tone === "oversleep"
        ? "寝坊・理想+" + (value - RULES.idealHappiness)
        : "理想まであと" + (RULES.idealHappiness - value);
}

function showHappinessChanges(
  playerIndex: 0 | 1,
  changes: readonly number[],
  speed: number,
): void {
  const stack = requireElement<HTMLElement>("#happiness-delta-stack-" + playerIndex);
  const visibleChanges = changes.filter((value) => value !== 0);
  const safeSpeed = Math.max(0.25, speed || 1);
  const runId = String(performance.now());
  stack.dataset.runId = runId;
  stack.dataset.lastChanges = JSON.stringify(visibleChanges);
  stack.replaceChildren(...visibleChanges.map((value, index) => {
    const label = document.createElement("span");
    label.className = "happiness-delta " + (value > 0 ? "positive" : "negative");
    label.textContent = signed(value);
    label.style.setProperty("--delta-index", String(index));
    label.style.setProperty("--delta-duration", (900 / safeSpeed) + "ms");
    label.style.setProperty("--delta-delay", (index * 90 / safeSpeed) + "ms");
    return label;
  }));

  const clearAfter = (1050 + Math.max(0, visibleChanges.length - 1) * 90) / safeSpeed;
  window.setTimeout(() => {
    if (stack.dataset.runId === runId) stack.replaceChildren();
  }, clearAfter);
}

function getBattleScene(currentGame: Phaser.Game): BattleScene {
  return currentGame.scene.getScene(SCENE_KEYS.battle) as BattleScene;
}

function getActiveBattleScene(): BattleScene | null {
  if (!game) return null;
  try {
    return getBattleScene(game);
  } catch {
    return null;
  }
}

function signed(value: number): string {
  return value > 0 ? "+" + value : String(value);
}

function requireElement<T extends Element>(selector: string): T {
  const element = document.querySelector<T>(selector);
  if (!element) throw new Error("Required element not found: " + selector);
  return element;
}

function escapeHtml(value: string): string {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll("\"", "&quot;")
    .replaceAll("'", "&#039;");
}
